import { ApplicationInsights, DistributedTracingModes } from "@microsoft/applicationinsights-web";

// Browser-side collector for the shared Azure Application Insights resource. The API and both SPAs
// feed the same instance; each is told apart by its cloud role name so the Admin "Platform
// Performance" page can split metrics per component.
//
// Telemetry only turns on when a connection string is present in the runtime config, so local dev
// (and any environment without it) stays silent. The Application Insights connection string is
// write-only ingestion — safe to expose to the browser by design.

let appInsights: ApplicationInsights | undefined;

export const initTelemetry = (connectionString: string | undefined, roleName: string): void => {
  if (!connectionString || appInsights) {
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        // SPA route changes → page views.
        enableAutoRouteTracking: true,
        // Correlate browser AJAX with the API's server-side spans (W3C traceparent, matching the
        // backend Azure Monitor OpenTelemetry distro). The API allows any header via CORS.
        enableCorsCorrelation: true,
        distributedTracingMode: DistributedTracingModes.W3C,
        disableFetchTracking: false,
        enableUnhandledPromiseRejectionTracking: true,
        // The legacy `unload` event is blocked by Permissions-Policy (and deprecation-warned by
        // Chrome), producing a console violation on every load. Drop it from the unload hooks — the
        // SDK still flushes on `pagehide`/`beforeunload`/`visibilitychange`, so no telemetry is lost.
        disablePageUnloadEvents: ["unload"],
      },
    });
    appInsights.loadAppInsights();
    // Stamp every item with the cloud role name.
    appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags = envelope.tags || {};
      envelope.tags["ai.cloud.role"] = roleName;
    });
    appInsights.trackPageView();
  } catch {
    // Telemetry must never break app startup.
    appInsights = undefined;
  }
};

export const getAppInsights = (): ApplicationInsights | undefined => appInsights;
